/**
 * Local Thumbnail Service
 * Provides fast thumbnail access using localStorage cache
 */

import cacheWarmingService from './cacheWarmingService';

class LocalThumbnailService {
    constructor() {
        this.cacheService = cacheWarmingService;
    }

    /**
     * Get thumbnails for specific project IDs
     * Uses localStorage cache for instant access
     */
    getThumbnailsForProjects(projectIds) {
        // Check if cache is ready
        if (!this.cacheService.isCacheReady()) {
            console.warn('⚠️ Thumbnail cache not ready, returning empty thumbnails');
            return {};
        }

        // Get thumbnails from localStorage
        return this.cacheService.getThumbnailsForProjects(projectIds);
    }

    /**
     * Get thumbnail for a single project
     */
    getThumbnailForProject(projectId) {
        const thumbnails = this.getThumbnailsForProjects([projectId]);
        return (thumbnails[projectId] ?? thumbnails[String(projectId)]) || null;
    }

    /**
     * Get all cached thumbnails
     */
    getAllThumbnails() {
        if (!this.cacheService.isCacheReady()) {
            console.warn('⚠️ Thumbnail cache not ready, returning empty thumbnails');
            return {};
        }

        return this.cacheService.getAllCachedThumbnails();
    }

    /**
     * Check if cache is ready
     */
    isCacheReady() {
        return this.cacheService.isCacheReady();
    }

    /**
     * Get cache status
     */
    getCacheStatus() {
        return this.cacheService.getWarmingStatus();
    }

    /**
     * Force refresh cache
     */
    forceRefreshCache() {
        this.cacheService.forceRefreshCache();
    }

    /**
     * Get cache statistics
     */
    getCacheStats() {
        const status = this.cacheService.getWarmingStatus();
        const thumbnails = this.getAllThumbnails();
        
        return {
            isReady: this.isCacheReady(),
            totalThumbnails: Object.keys(thumbnails).length,
            cacheAge: status.cacheStatus?.cache_age_seconds || 0,
            isWarming: status.isWarming,
            elapsedTime: status.elapsedTime
        };
    }
}

// Create singleton instance
const localThumbnailService = new LocalThumbnailService();

export default localThumbnailService;
